import type { UseHeadInput, UseSeoMetaInput } from '@unhead/vue'
import { useConfigStore } from '~/store/useConfigStore'
import type { ContentBlock, Image, Menu, Sponsor, Team, NextEventBanner } from '~/types'
import { ACCENT_COLOR, WHITE_COLOR } from '~/shared/constants'
import { highestContrastRatio } from '~/shared/lib/colors'

export const useTeam = (dataRef: MaybeRefOrGetter<Maybe<Team.Model | undefined>>) => {
  const data = ref<Maybe<Team.Model>>()

  const configStore = useConfigStore()
  const { defaultTimezone } = storeToRefs(configStore)

  const team = computed<Maybe<Team.Model>>(() => unref(data) || null)
  const id = computed<Maybe<number>>(() => unref(data)?.id || null)
  const name = computed<Maybe<string>>(() => unref(data)?.name || null)
  const slug = computed<Maybe<string>>(() => unref(data)?.slug || null)
  const metaTitle = computed<Maybe<string>>(() => unref(data)?.metaTitle || null)
  const metaDescription = computed<Maybe<string>>(() => unref(data)?.metaDescription || null)
  const metaKeywords = computed<Maybe<string>>(() => unref(data)?.metaKeywords || null)
  const altTitle = computed<Maybe<string>>(() => unref(data)?.altTitle || null)
  const hideTitleSection = computed<boolean>(() => !!unref(data)?.hideTitleSection)
  const hideInDropdown = computed<boolean>(() => !!unref(data)?.hideInDropdown)
  const color = computed<string>(() => unref(data)?.color || WHITE_COLOR)
  const textColor = computed<string>(() => unref(data)?.textColor || highestContrastRatio(color.value))
  const textHoverColor = computed<string>(() => unref(data)?.textHoverColor || ACCENT_COLOR)
  const timezone = computed<string>(() => unref(data)?.timezone || defaultTimezone.value)
  const analyticsCode = computed<Maybe<string>>(() => unref(data)?.analyticsCode || null)
  const contentBlocks = computed<ContentBlock.Model[]>(() => unref(data)?.contentBlocks || [])
  const menu = computed<Maybe<Menu.Model>>(() => unref(data)?.menus?.[0] || null)
  const logo = computed<Maybe<Image>>(() => unref(data)?.logo || null)
  const metaImage = computed<Maybe<Image>>(() => unref(data)?.metaImage || null)
  const hasContentBlocks = computed<boolean>(() => contentBlocks.value?.length > 0)
  const nextEventBanners = computed<NextEventBanner.Model[]>(() => unref(data)?.nextEventBanners || [])
  const scheduleEventBackground = computed<Maybe<Image>>(() => unref(data)?.scheduleEventBackground || null)
  const slotContentBlocks = computed<ContentBlock.Model[]>(() => menu.value?.slotContentBlocks || [])
  const hasSlotContentBlocks = computed<boolean>(() => slotContentBlocks.value.length > 0)

  const sponsors = computed<Sponsor.Model[]>(() => {
    const contentBlocks: ContentBlock.Model<Sponsor.Model>[] = unref(data)?.sponsors ?? []
    return contentBlocks.map(item => item.data)
  })

  const headInput = computed<UseHeadInput>(() => {
    if (!team.value) {
      return {}
    }

    return {
      title: name.value,
    }
  })

  const seoMetaInput = computed<UseSeoMetaInput>(() => (
    {
      title: name.value,
      ogTitle: metaTitle || name.value,
      description: metaDescription.value,
      ogDescription: metaDescription.value,
      ogImage: metaImage.value?.src || logo.value?.src,
    }
  ))

  const styles = computed<Record<string, string | undefined>>(() => ({
    '--team-color': color.value,
    '--secondary-color': textColor.value,
    '--accent-color': textHoverColor.value,
  }))
  const xLink = computed<Maybe<string>>(() => unref(data)?.xLink || null)
  const youtubeLink = computed<Maybe<string>>(() => unref(data)?.youtubeLink || null)
  const instagramLink = computed<Maybe<string>>(() => unref(data)?.instagramLink || null)
  const facebookLink = computed<Maybe<string>>(() => unref(data)?.facebookLink || null)
  const tiktokLink = computed<Maybe<string>>(() => unref(data)?.tiktokLink || null)

  const adCode = computed<Maybe<string>>(() => unref(data)?.adCode || null)
  const adId = computed<Maybe<string>>(() => unref(data)?.adId || null)

  watchEffect(() => {
    data.value = toValue(dataRef)
  })

  return {
    adCode,
    adId,
    altTitle,
    analyticsCode,
    color,
    contentBlocks,
    facebookLink,
    hasContentBlocks,
    hasSlotContentBlocks,
    headInput,
    hideInDropdown,
    hideTitleSection,
    id,
    instagramLink,
    logo,
    menu,
    metaDescription,
    metaKeywords,
    metaTitle,
    name,
    nextEventBanners,
    scheduleEventBackground,
    seoMetaInput,
    slotContentBlocks,
    slug,
    styles,
    sponsors,
    team,
    textColor,
    textHoverColor,
    tiktokLink,
    timezone,
    xLink,
    youtubeLink,
  }
}
